import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { X, Minus, Plus, Loader2, Tag, ArrowRight, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCE_LABEL = {
  everyone: "Everyone",
  couples: "Couples",
  tv: "TV",
  single_guys: "Single Guys",
  single_ladies: "Single Ladies",
  single_guy: "Single Man",
  single_lady: "Single Lady",
  couple: "Couple",
};

const MEMBERSHIP_TIERS = ["single_guy", "single_lady", "couple", "tv"];

const fmtGBP = (n) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(n || 0);

export default function CheckoutModal({ open, onClose, venue, event }) {
  const requireMembership = !!event?.require_membership;
  const membershipPricing = event?.membership_pricing || {};
  const audiences = requireMembership
    ? MEMBERSHIP_TIERS.filter((t) => Number(membershipPricing[t]) > 0)
    : event?.audiences || [];
  const pricing = requireMembership ? membershipPricing : event?.pricing || {};

  const [qty, setQty] = useState({});
  const [discount, setDiscount] = useState("");
  const [buyer, setBuyer] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    fabswingers_username: "",
    swinghub_profile: "",
    membership_number: "",
    show_public: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    if (open) {
      const init = {};
      audiences.forEach((a) => (init[a] = 0));
      setQty(init);
      setDiscount("");
      setSubmitting(false);
      setTermsAccepted(false);
    }
  }, [open, event?.id]);

  const totals = useMemo(() => {
    const subtotal = audiences.reduce(
      (s, a) => s + (Number(pricing[a]) || 0) * (Number(qty[a]) || 0),
      0
    );
    const totalQty = audiences.reduce((s, a) => s + (Number(qty[a]) || 0), 0);
    return { subtotal, totalQty };
  }, [audiences, pricing, qty]);

  if (!open) return null;

  const bump = (a, delta) =>
    setQty((q) => ({ ...q, [a]: Math.max(0, Math.min(20, (q[a] || 0) + delta)) }));

  const hasProfile =
    buyer.fabswingers_username.trim().length > 0 || buyer.swinghub_profile.trim().length > 0;

  const hasMembershipNumber = buyer.membership_number.trim().length > 0;

  const canSubmit =
    totals.totalQty > 0 &&
    buyer.first_name.trim() &&
    buyer.last_name.trim() &&
    /.+@.+\..+/.test(buyer.email) &&
    buyer.phone.trim().length >= 5 &&
    hasProfile &&
    (!requireMembership || hasMembershipNumber) &&
    termsAccepted &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      const line_items = audiences
        .filter((a) => (qty[a] || 0) > 0)
        .map((a) => ({ tier: a, quantity: qty[a] }));
      const { data } = await axios.post(`${API}/checkout/create`, {
        venue_slug: venue.slug,
        event_slug: event.slug,
        line_items,
        discount_code: discount.trim() || null,
        buyer: {
          ...buyer,
          membership_number: buyer.membership_number.trim() || null,
        },
        origin_url: window.location.origin,
      });
      if (data?.checkout_url) {
        window.location.href = data.checkout_url;
        return;
      }
      toast.error("Unable to start checkout. Please try again.");
      setSubmitting(false);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Checkout failed.";
      toast.error(msg);
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[100] flex items-stretch md:items-center justify-center bg-black/80 backdrop-blur-sm"
      data-testid="checkout-modal"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full md:max-w-2xl md:max-h-[92vh] overflow-y-auto bg-[#0A0A0A] border border-white/10 text-[#F5F5F0]"
      >
        {/* Header */}
        <div className="flex items-start justify-between px-6 md:px-8 py-5 border-b border-white/10 sticky top-0 bg-[#0A0A0A]/95 backdrop-blur z-10">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.28em] text-[#D9A6F5] mb-1">Get tickets</div>
            <div
              data-testid="checkout-event-title"
              className="text-xl md:text-2xl font-black tracking-tight truncate"
            >
              {event?.name}
              {venue?.name && (
                <span className="text-[#A3A3A3] font-light"> @ {venue.name}</span>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="checkout-close"
            className="ml-4 text-[#A3A3A3] hover:text-[#F5F5F0] transition-colors"
            aria-label="Close"
          >
            <X size={22} />
          </button>
        </div>

        <form onSubmit={submit} className="px-6 md:px-8 py-6 space-y-8">
          {/* Tier steppers */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-3">
              {requireMembership ? "Members-only · choose your tickets" : "Choose your tickets"}
            </div>
            {audiences.length === 0 && (
              <div
                data-testid="checkout-no-tiers"
                className="border border-white/10 bg-black px-4 py-6 text-sm text-[#A3A3A3] text-center"
              >
                {requireMembership
                  ? "This event is members-only. Prices haven't been set for any membership type yet — please contact the venue."
                  : "No ticket tiers available yet."}
              </div>
            )}
            <div className="border border-white/10 divide-y divide-white/10">
              {audiences.map((a) => {
                const p = Number(pricing[a]) || 0;
                const q = qty[a] || 0;
                return (
                  <div
                    key={a}
                    data-testid={`checkout-tier-${a}`}
                    className="flex items-center justify-between px-4 py-4"
                  >
                    <div>
                      <div className="text-sm md:text-base font-bold text-[#F5F5F0]">
                        {AUDIENCE_LABEL[a] || a}
                      </div>
                      <div className="text-xs text-[#A3A3A3]">{fmtGBP(p)} each</div>
                    </div>
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        data-testid={`checkout-decr-${a}`}
                        onClick={() => bump(a, -1)}
                        disabled={q <= 0}
                        className="h-9 w-9 border border-white/20 flex items-center justify-center text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] disabled:opacity-30 disabled:hover:border-white/20 disabled:hover:text-[#F5F5F0] transition-colors"
                        aria-label={`Decrease ${AUDIENCE_LABEL[a] || a}`}
                      >
                        <Minus size={14} />
                      </button>
                      <div
                        className="w-6 text-center text-base font-black"
                        data-testid={`checkout-qty-${a}`}
                      >
                        {q}
                      </div>
                      <button
                        type="button"
                        data-testid={`checkout-incr-${a}`}
                        onClick={() => bump(a, 1)}
                        className="h-9 w-9 border border-white/20 flex items-center justify-center text-[#F5F5F0] hover:border-[#D9A6F5] hover:text-[#D9A6F5] transition-colors"
                        aria-label={`Increase ${AUDIENCE_LABEL[a] || a}`}
                      >
                        <Plus size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Discount */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-3">
              Discount code
            </div>
            <div className="relative">
              <Tag size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A3A3A3]" />
              <input
                type="text"
                value={discount}
                onChange={(e) => setDiscount(e.target.value.toUpperCase())}
                placeholder="Optional"
                data-testid="checkout-discount"
                className="w-full bg-black border border-white/15 h-11 pl-10 pr-3 text-[#F5F5F0] uppercase tracking-widest text-sm focus:outline-none focus:border-[#D9A6F5]"
              />
            </div>
          </div>

          {/* Buyer info */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-3">
              Your details
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <input
                required
                type="text"
                placeholder="First name"
                value={buyer.first_name}
                onChange={(e) => setBuyer({ ...buyer, first_name: e.target.value })}
                data-testid="checkout-first-name"
                className="bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
              />
              <input
                required
                type="text"
                placeholder="Last name"
                value={buyer.last_name}
                onChange={(e) => setBuyer({ ...buyer, last_name: e.target.value })}
                data-testid="checkout-last-name"
                className="bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
              />
              <input
                required
                type="email"
                placeholder="Email"
                value={buyer.email}
                onChange={(e) => setBuyer({ ...buyer, email: e.target.value })}
                data-testid="checkout-email"
                className="md:col-span-2 bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
              />
              <input
                required
                type="tel"
                placeholder="Mobile phone"
                value={buyer.phone}
                onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })}
                data-testid="checkout-phone"
                className="md:col-span-2 bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
              />
            </div>
          </div>

          {/* Membership number (only for members-only events) */}
          {requireMembership && (
            <div data-testid="checkout-membership-wrap">
              <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-1">
                Membership number
              </div>
              <p className="text-[11px] text-[#A3A3A3]/80 mb-3">
                This event is <span className="text-[#D9A6F5]">members-only</span>. Enter the
                membership number issued to you by {venue?.name || "the venue"}.
              </p>
              <input
                required
                type="text"
                placeholder="e.g. M-2381"
                value={buyer.membership_number}
                onChange={(e) => setBuyer({ ...buyer, membership_number: e.target.value })}
                data-testid="checkout-membership-number"
                className="w-full bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] uppercase tracking-widest text-sm focus:outline-none focus:border-[#D9A6F5]"
              />
              {venue?.memberships_enabled && (
                <p className="mt-2 text-[11px] text-[#A3A3A3]">
                  Not a member yet?{" "}
                  <a
                    href={`/v/${venue.slug}/membership`}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="checkout-buy-membership-link"
                    className="text-[#D9A6F5] hover:underline"
                  >
                    Purchase your membership here first
                  </a>
                  .
                </p>
              )}
            </div>
          )}

          {/* Profile verification */}
          <div>
            <div className="text-[11px] uppercase tracking-[0.28em] text-[#A3A3A3] mb-1">
              Profile verification
            </div>
            <p className="text-[11px] text-[#A3A3A3]/80 mb-3">
              Please provide <span className="text-[#D9A6F5]">at least one</span> so the venue can
              verify you're a real member.
            </p>
            <div className="grid grid-cols-1 gap-3">
              <input
                type="text"
                placeholder="FabSwingers username"
                value={buyer.fabswingers_username}
                onChange={(e) => setBuyer({ ...buyer, fabswingers_username: e.target.value })}
                data-testid="checkout-fabswingers"
                className="bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
              />
              <input
                type="url"
                placeholder="SwingHub profile link (https://…)"
                value={buyer.swinghub_profile}
                onChange={(e) => setBuyer({ ...buyer, swinghub_profile: e.target.value })}
                data-testid="checkout-swinghub"
                className="bg-black border border-white/15 h-11 px-3 text-[#F5F5F0] focus:outline-none focus:border-[#D9A6F5]"
              />
            </div>
            {!hasProfile && buyer.first_name && (
              <p
                data-testid="checkout-profile-hint"
                className="mt-2 text-[11px] text-[#D9A6F5]"
              >
                Add either a FabSwingers username or a SwingHub profile link to continue.
              </p>
            )}

            {/* Show in public 'Who's Going' */}
            <label
              className={`mt-4 flex items-start gap-3 border border-white/10 hover:border-[#D9A6F5]/40 p-3 cursor-pointer transition-colors ${
                hasProfile ? "" : "opacity-50 cursor-not-allowed"
              }`}
              data-testid="checkout-show-public-wrap"
            >
              <input
                type="checkbox"
                checked={buyer.show_public}
                disabled={!hasProfile}
                onChange={(e) => setBuyer({ ...buyer, show_public: e.target.checked })}
                data-testid="checkout-show-public"
                className="mt-0.5 h-4 w-4 accent-[#D9A6F5]"
              />
              <div className="text-[12px] leading-relaxed">
                <div className="text-[#F5F5F0]">
                  Show in the <span className="text-[#D9A6F5]">Who&apos;s Going</span> section &amp;
                  public guest list
                </div>
                <div className="text-[11px] text-[#A3A3A3] mt-0.5">
                  Your profile link will be visible to other members on the event page. Uncheck
                  to stay private.
                </div>
              </div>
            </label>
          </div>

          {/* Summary */}
          <div className="border border-white/10 bg-black px-5 py-4">
            <div className="space-y-1.5 text-[12px] text-[#A3A3A3]">
              <div className="flex items-center justify-between">
                <span>Tickets ({totals.totalQty})</span>
                <span data-testid="checkout-subtotal">{fmtGBP(totals.subtotal)}</span>
              </div>
              {!event?.hide_booking_fees && (
                <div className="flex items-center justify-between">
                  <span>Booking fee (£1.95 × {totals.totalQty})</span>
                  <span data-testid="checkout-booking-fee">
                    {fmtGBP((totals.totalQty * 195) / 100)}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10 flex items-baseline justify-between">
              <div className="text-xs uppercase tracking-[0.28em] text-[#A3A3A3]">
                Total
              </div>
              <div
                data-testid="checkout-total"
                className="text-2xl md:text-3xl font-black tracking-tighter text-[#F5F5F0]"
              >
                {fmtGBP(
                  totals.subtotal +
                    (event?.hide_booking_fees ? 0 : (totals.totalQty * 195) / 100)
                )}
              </div>
            </div>
            {discount && (
              <div className="text-[11px] text-[#A3A3A3] mt-2">
                Discount code applied at checkout if valid.
              </div>
            )}
          </div>

          {/* T&Cs — required */}
          <label
            className="flex items-start gap-3 border border-white/10 hover:border-[#D9A6F5]/40 p-3.5 cursor-pointer transition-colors"
            data-testid="checkout-terms-wrap"
          >
            <input
              type="checkbox"
              checked={termsAccepted}
              onChange={(e) => setTermsAccepted(e.target.checked)}
              data-testid="checkout-terms-checkbox"
              className="mt-1 h-4 w-4 accent-[#D9A6F5] flex-shrink-0"
              required
            />
            <span className="text-[12px] text-[#A3A3A3] leading-relaxed">
              I have read and agree to the{" "}
              <a
                href="/terms/tickets"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="checkout-terms-link"
                className="text-[#D9A6F5] hover:underline font-bold"
              >
                Ticket Purchase Terms &amp; Conditions
              </a>
              .
            </span>
          </label>

          {/* Submit */}
          <button
            type="submit"
            disabled={!canSubmit}
            data-testid="checkout-submit"
            className="w-full inline-flex items-center justify-center gap-2 bg-[#D9A6F5] text-black hover:bg-[#E8C8FF] disabled:bg-white/10 disabled:text-[#666] disabled:cursor-not-allowed px-6 py-4 text-xs uppercase tracking-[0.24em] font-bold transition-colors"
          >
            {submitting ? (
              <>
                <Loader2 size={14} className="animate-spin" /> Redirecting…
              </>
            ) : (
              <>
                Continue to secure payment <ArrowRight size={14} />
              </>
            )}
          </button>

          <div className="flex items-center justify-center gap-6 text-[10px] uppercase tracking-[0.22em] text-[#A3A3A3]">
            <span className="inline-flex items-center gap-2">
              <ShieldCheck size={12} className="text-[#D9A6F5]" /> Secure checkout
            </span>
            <span className="inline-flex items-center gap-2">
              <Lock size={12} className="text-[#D9A6F5]" /> Powered by Stripe
            </span>
          </div>
        </form>
      </div>
    </div>
  );
}
